import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

import type { SourceAutodocItem } from '@/api/types/node';
import { type AutodocLink, AutodocsTable } from '@/components/Autodocs/AutodocsTable';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { usePdf } from '@/contexts/PdfContext';
import { useVersions } from '@/contexts/VersionsContext';
import { searchParam } from '@/utils/router';

const autodocsSearchSchema = z.object({
  pageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  pageIndex: z.number().default(0).catch(0),
  search: searchParam().optional(),
  type: z.enum(['all', 'autodoc', 'page']).optional(),
  tag_ids: z.array(z.string()).optional(),
  versionId: searchParam().optional(),
  sorting: z
    .array(
      z.object({
        id: z.string(),
        desc: z.boolean(),
      })
    )
    .default([{ id: 'updated_at', desc: true }])
    .catch([{ id: 'updated_at', desc: true }]),
});

export const Route = createFileRoute('/$organizationName/pdf/$pdfVersionNodeId/autodocs/')({
  component: AutodocsListPage,
  validateSearch: autodocsSearchSchema,
});

function AutodocsListPage() {
  const { organizationName, pdfVersionNodeId } = Route.useParams();
  const { isPdfLoading } = usePdf();
  const { versionId } = Route.useSearch();
  const { isVersionsLoading, sourceId } = useVersions();

  const getAutodocLink = useCallback(
    (item: SourceAutodocItem): AutodocLink => {
      if (item.type === 'autodoc') {
        return {
          to: '/$organizationName/pdf/$pdfVersionNodeId/autodocs/$autodocId',
          params: { autodocId: item.id, organizationName, pdfVersionNodeId },
          search: versionId ? { versionId } : undefined,
        };
      }
      return {
        to: '/$organizationName/pdf/$pdfVersionNodeId/autodocs/page/$pageVersionNodeId',
        params: { organizationName, pageVersionNodeId: item.rootVersionNodeId || '', pdfVersionNodeId },
        search: versionId ? { versionId } : undefined,
      };
    },
    [organizationName, pdfVersionNodeId, versionId]
  );

  const isSourceLoading = isPdfLoading || isVersionsLoading;

  return (
    <AutodocsTable
      getAutodocLink={getAutodocLink}
      emptyStateDescription="Generate autodocs to get AI-powered documentation for your PDF."
      isSourceLoading={isSourceLoading}
      route={Route}
      versionNodeId={pdfVersionNodeId}
      sourceId={sourceId}
    />
  );
}
